import {
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  useIsMobile,
} from '@design-system';

import {
  InviteClientTabs,
  InviteClientContext,
  useInviteClientProvider,
  useInviteClientTrigger,
  useInviteClientStore,
  EmailDeliverabilityWarningModal,
  InviteLinkCreatedModal,
  PrefillAnswersSection,
} from '@/features/invite-client';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { useIsEdjPilotAdvisor } from '@/shared/hooks/use-edj-pilot-advisor';
import { usePilotSelfPlanIntakeForm } from '@/shared/hooks/use-active-pilot-program';

interface InviteClientDialogContentProps {
  title?: string;
  description?: string;
}

function InviteClientDialogContent({
  title = 'Invite a client',
  description = 'Send an invitation to help your clients plan for long-term care',
}: InviteClientDialogContentProps) {
  const isMobile = useIsMobile();
  const { isInviteClientOpen, clearInviteClientParams } = useInviteClientTrigger();
  const {
    showAdditionalSettings,
    showEmailWarningModal,
    showPrefillAnswers,
    setShowPrefillAnswers,
  } = useInviteClientStore();

  const showStackedDialog = showAdditionalSettings || showEmailWarningModal;

  return (
    <>
      {!isMobile ? (
        <Dialog
          open={isInviteClientOpen}
          onOpenChange={open => {
            if (!open) {
              setShowPrefillAnswers(false);
              clearInviteClientParams();
            }
          }}
        >
          <DialogContent
            className={cn('max-h-[90vh] w-full transition-all duration-200', {
              'overflow-y-auto sm:max-w-xl': !showPrefillAnswers,
              'max-h-144 min-h-144 -translate-y-[calc(50%+1rem)] scale-[0.94]': showStackedDialog,
              'flex flex-col overflow-hidden sm:max-w-3xl': showPrefillAnswers,
            })}
          >
            {showPrefillAnswers ? (
              <>
                <DialogHeader className="sr-only">
                  <DialogTitle>Pre-fill answers</DialogTitle>
                  <DialogDescription>{description}</DialogDescription>
                </DialogHeader>
                <PrefillAnswersSection />
              </>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle>{title}</DialogTitle>
                  <DialogDescription>{description}</DialogDescription>
                </DialogHeader>
                <InviteClientTabs />
              </>
            )}
          </DialogContent>
        </Dialog>
      ) : (
        <Drawer
          open={isInviteClientOpen}
          onOpenChange={open => {
            if (!open) {
              setShowPrefillAnswers(false);
              clearInviteClientParams();
            }
          }}
        >
          <DrawerContent
            className={cn('max-h-[90vh]! transition-all! duration-200! px-4 pb-6', {
              'overflow-y-auto': !showPrefillAnswers,
              'h-144 bottom-1! scale-[0.96]': showStackedDialog,
              'flex flex-col overflow-hidden': showPrefillAnswers,
            })}
          >
            {showPrefillAnswers ? (
              <>
                <DrawerHeader className="sr-only">
                  <DrawerTitle>Pre-fill answers</DrawerTitle>
                  <DrawerDescription>{description}</DrawerDescription>
                </DrawerHeader>
                <PrefillAnswersSection />
              </>
            ) : (
              <>
                <DrawerHeader className="text-left">
                  <DrawerTitle>{title}</DrawerTitle>
                  <DrawerDescription>{description}</DrawerDescription>
                </DrawerHeader>
                <InviteClientTabs />
              </>
            )}
          </DrawerContent>
        </Drawer>
      )}

      <EmailDeliverabilityWarningModal />
      <InviteLinkCreatedModal />
    </>
  );
}

export interface InviteClientDialogProps extends InviteClientDialogContentProps {
  onInviteSuccess?: () => void;
}

export function InviteClientDialog({
  title,
  description,
  onInviteSuccess,
}: InviteClientDialogProps) {
  const { flags } = useFeatureFlags();
  const { clearInviteClientParams } = useInviteClientTrigger();
  const storeValue = useInviteClientProvider({
    onInviteSuccess,
    onClose: clearInviteClientParams,
  });

  const isEdjPilot = useIsEdjPilotAdvisor();
  const hasSelfPlanIntakeForm = !!usePilotSelfPlanIntakeForm();
  const hideEmailAndFillIntakeForm = flags?.hideInviteEmailAndFillIntakeForm;

  const dialogDescription = hasSelfPlanIntakeForm
    ? 'Try Waterlily for yourself or share a link with your clients.'
    : hideEmailAndFillIntakeForm || isEdjPilot
      ? 'Get a unique link that you can share with clients through text, social media, or in person.'
      : description;

  return (
    <InviteClientContext.Provider value={storeValue}>
      <InviteClientDialogContent
        title={title}
        description={dialogDescription}
      />
    </InviteClientContext.Provider>
  );
}
